import {
  Box,
  Grid,
  IconButton,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormControl,
  FormLabel,
  MenuItem,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Button,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Input from "../../../../../../components/Input";
import { useUserStore } from "../../../../../../store/useUserStore";
import { useBankNames } from "../../../../../../hooks/useBankNames";
import Autocomplete from "@mui/material/Autocomplete";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useEffect, useState } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import { useTenPercentClaimFormStore } from "../../../../../../store/useTenPercentClaimFormStore";
import FileUpload from "../../../../../../components/FileUpload";
import { getDocument } from "../../../../../../services/dashboard";
import { useToast } from "../../../../../../hooks/useToast";

export default function BankingForm({
  bankingInfoData,
  iceClaimNumber,
  onTenPercentBankingCommentChange,
  documentData,
  isAssigned,
  claimStatus,
  tenPercentBankingComment,
  commentData,
  isEditable,
  handleBankingInfoFieldChange,
  policyNumber,
  onDocumentDeleteChange,
}: any) {
  const [localDocs, setLocalDocs] = useState<any[]>(documentData);
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);
  const [fileErrors, setFileErrors] = useState("");

  const { addTenPercentDocument } = useTenPercentClaimFormStore();
  const { removeTenPercentDocument } = useTenPercentClaimFormStore();
  const uploadedFile = useTenPercentClaimFormStore(
    (state) => state.tenPercentdocuments["bank_proof"]
  );

  const role = useUserStore((state) => state.role);
  const { bankOptions, loading } = useBankNames();

  const {showInfoToast} = useToast()

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const handleDeleteDoc = (index: number) => {
    const doc = localDocs[index];

    removeTenPercentDocument("bank_proof");

    const updatedDocs = [...localDocs];
    updatedDocs.splice(index, 1);
    setLocalDocs(updatedDocs);

    if (onDocumentDeleteChange) {
      onDocumentDeleteChange(doc.key);
    }
  };

  useEffect(() => {
    if (uploadedFile) {
      setLocalDocs([
        {
          file_name: uploadedFile.name,
          file_url: URL.createObjectURL(uploadedFile),
          isLocal: true,
        },
      ]);
    } else {
      setLocalDocs(documentData);
    }
  }, [uploadedFile]);

  const comments = commentData?.filter(
    (comment: any) => comment.section_type === "BANKING_INFORMATION"
  );

  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}-${String(
      d.getMonth() + 1
    ).padStart(2, "0")}-${d.getFullYear()}`;
  };

  const handlePreview = async (doc: any) => {
    try {
      if (doc.isLocal) {
        setSelectedDoc(doc);
        setOpenPreview(true);
      } else {
        const res = await getDocument(iceClaimNumber, doc.document_id);
        if (!res || res.status !== 200) {
          showInfoToast("Something went wrong")
          throw new Error("Failed to fetch document");
        }

        const blob = res.data;
        const url = URL.createObjectURL(blob);

        setSelectedDoc({ ...doc, file_url: url });
        setOpenPreview(true);
      }
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  return (
    <Grid
      container
      rowSpacing={5}
      columnSpacing={10}
      mt={1}
      textAlign="left"
      p={3}
    >
      <Grid size={{ xs: 12, md: 4 }}>
        <Autocomplete
          freeSolo
          disabled={!isEditable}
          options={bankOptions}
          loading={loading}
          value={bankingInfoData?.bank_name || ""}
          onInputChange={(e, newValue) =>
            handleBankingInfoFieldChange("bank_name", newValue)
          }
          openOnFocus
          popupIcon={null}
          renderInput={(params) => (
            <Input
              {...params}
              fullWidth
              height={40}
              width="100%"
              label="Bank name"
              variant="outlined"
              backgroundColor={
                isEditable ? "background.default" : "background.paper"
              }
              inputProps={{
                ...params.inputProps,
                style: {
                  height: "20px",
                  padding: "10px 14px",
                  fontSize: "14px",
                },
              }}
            />
          )}
          ListboxProps={{
            sx: {
              zIndex: 1300,
              maxHeight: 200,
              backgroundColor: "background.paper",
              border: "1px solid #ddd",
              boxShadow: 3,
              fontSize: "14px",
            },
          }}
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfoData?.account_name}
          height={40}
          width="100%"
          label="Account name"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingInfoFieldChange("account_name", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfoData?.branch_code}
          height={40}
          width="100%"
          label="Branch code"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingInfoFieldChange("branch_code", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          select
          fullWidth
          label="Account type"
          variant="outlined"
          value={bankingInfoData?.account_type}
          onChange={(e: any) =>
            handleBankingInfoFieldChange("account_type", e.target.value)
          }
          disabled={!isEditable}
          width="100%"
          height={40}
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
        >
          <MenuItem disabled value="" sx={{ background: "background.default" }}>
            -Account Type-
          </MenuItem>
          <MenuItem
            value="Cheque Account"
            sx={{
              background: "background.default",
              borderBottom: "1px solid #DFDDDD",
            }}
          >
            Cheque Account
          </MenuItem>
          <MenuItem
            value="Transmission Account"
            sx={{
              background: "background.default",
              borderBottom: "1px solid #DFDDDD",
            }}
          >
            Transmission Account
          </MenuItem>
          <MenuItem
            value="Saving Account"
            sx={{ background: "background.default" }}
          >
            Saving Account
          </MenuItem>
        </Input>
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfoData?.account_number}
          height={40}
          width="100%"
          label="Account number"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingInfoFieldChange("account_number", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={bankingInfoData?.branch_name}
          height={40}
          width="100%"
          label="Branch Name"
          backgroundColor={
            isEditable ? "background.default" : "background.paper"
          }
          onChange={(e: any) =>
            handleBankingInfoFieldChange("branch_name", e.target.value)
          }
        />
      </Grid>

      <Grid size={{ xs: 12, md: 4 }}>
        <Input
          disabled={!isEditable}
          value={policyNumber}
          height={40}
          width="100%"
          label="Policy number"
          backgroundColor="background.paper"
          onChange={(e: any) =>
            handleBankingInfoFieldChange("branch_code", e.target.value)
          }
        />
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <FormControl component="fieldset" fullWidth>
          <FormLabel
            component="legend"
            sx={{
              fontWeight: "bold",
              color: "text.primary",
              "&.Mui-focused": {
                color: "text.primary",
              },
            }}
          >
            Verified
          </FormLabel>
          <RadioGroup
            row
            name="is_verified"
            value={bankingInfoData?.is_verified?.toString()}
            onChange={(e) =>
              handleBankingInfoFieldChange("is_verified", e.target.value)
            }
          >
            <FormControlLabel
              value="true"
              control={<Radio disabled={!(role === "consultant")} />}
              label="Yes"
            />
            <FormControlLabel
              value="false"
              control={<Radio disabled={!(role === "consultant")} />}
              label="No"
            />
          </RadioGroup>
        </FormControl>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Box width={270} height={48} mb={10}>
          <Typography fontWeight={600} textAlign="left" fontSize={18}>
            Proof of Banking
          </Typography>
          {localDocs.map((doc: any, index: number) => (
            <Box
              key={index}
              sx={{
                background: "#F5EEE9",
                color: "#FF0000",
                borderRadius: 1,
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 2,
              }}
            >
              <PictureAsPdfIcon />
              <Box>
                <Typography fontSize={14}>{doc.file_name}</Typography>
                <Typography fontSize={12} color="text.secondary"></Typography>
              </Box>
              {isEditable ? (
                <IconButton onClick={() => handleDeleteDoc(index)} size="small">
                  <DeleteIcon sx={{ color: "#FF0000" }} />
                </IconButton>
              ) : (
                <IconButton onClick={() => handlePreview(doc)} size="small">
                  <VisibilityIcon sx={{ color: "#EC6E00" }} />
                </IconButton>
              )}
            </Box>
          ))}
          {isEditable && localDocs.length === 0 && (
            <Box mt={2}>
              <FileUpload
                inputId="rma-policy"
                onChange={(e: any) => {
                  if (e.target.files.length > 0) {
                    const file = e.target.files[0];
                     if (file.size > 2 * 1024 * 1024) {
                    setFileErrors( "File must be less than 2MB")
                    return;
                  }
                    const newDoc = {
                      file_name: file.name,
                      file_url: URL.createObjectURL(file),
                      isLocal: true,
                    };
                    setLocalDocs([newDoc]);
                    addTenPercentDocument("bank_proof", file);
                  }
                }}
              />
               {fileErrors && (
              <Typography color="error" fontSize={14}>
                {fileErrors}
              </Typography>
            )}
            </Box>
          )}
        </Box>

        {comments?.length > 0 && (
          <Grid size={{ xs: 12 }}>
            <Typography fontWeight={600} fontSize={18} mb={2}>
              Comments
            </Typography>
            {comments.map((comment: any, index: number) => (
              <Accordion
                key={index}
                sx={{
                  backgroundColor: "#F7F7F7",
                  boxShadow: "none",
                  borderRadius: "6px",
                  mb: 2,
                  border: "1px solid #E0E0E0",
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  aria-controls={`panel${index}-content`}
                  id={`panel${index}-header`}
                  sx={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <Box>
                    <Typography fontWeight={600} fontSize={14}>
                      {comment.commentedByName}{" "}
                      <Typography
                        component="span"
                        fontWeight={600}
                        color="#EC6E00"
                      >
                        - {comment.role_at_time_of_comment?.toUpperCase()}
                      </Typography>
                    </Typography>
                    <Typography fontSize={12} color="text.secondary">
                      {new Date(comment.created_on).toLocaleTimeString(
                        "en-US",
                        {
                          hour: "numeric",
                          minute: "2-digit",
                        }
                      )}{" "}
                      | {formatDate(comment.created_on)}
                    </Typography>
                  </Box>
                </AccordionSummary>

                <AccordionDetails>
                  <Typography fontSize={15} color="#333" lineHeight={1.6}>
                    {comment.content}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Grid>
        )}

        {isAssigned && claimStatus !== "PAID" && (
          <Grid
            size={{
              xs: 12,
            }}
          >
            <Typography
              variant="body1"
              fontWeight={600}
              fontSize={18}
              sx={{ display: "block", textAlign: "left" }}
            >
              Comment
            </Typography>
            <TextField
              multiline
              rows={4}
              variant="outlined"
              fullWidth
              disabled={isEditable}
              value={tenPercentBankingComment}
              onChange={onTenPercentBankingCommentChange}
            />
          </Grid>
        )}
      </Grid>
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Grid>
  );
}
